import React, { useState } from 'react';
import LoadWhenVisible from 'components/load-when-visible';
import LoadingSkeleton from 'components/loading-skeleton';
import LoadingData from 'components/loading-data';
import Link from 'components/link';
import Button from 'components/control/button';
import { getDistance } from 'geolib';
import { useModalContext, useFeaturesContext } from 'contexts';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import SchemaOrg from 'components/schema-org';
import CheckmarkCircleIcon from 'components/icon/checkmark-circle-icon';
import CloseCircleIcon from 'components/icon/close-circle-icon';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_ADDRESS_PAGE_SCHOOL_NAME } from 'constants/events';
import { listingPageModalIds } from 'constants/test-constants';
import { getNearbySchools } from 'data/schools';
import { useThemeContext } from 'contexts/theme';
import { ThemeNames } from 'types/themes';

import School from 'data/schools';
import type Listing from 'data/listing';
import type { Point } from 'data/listing';

interface Props {
  listing: Listing;
  hasFetched: boolean;
  setHasFetched: (hasFetched: boolean) => void;
  setHasSchools: (hasSchools: boolean) => void;
  isModal?: boolean;
  schools?: Array<Record<string, unknown>>;
}

const distanceToMappable = (point1: Point, point2: Point) => {
  const distanceInMeters = getDistance({ lng: point1.longitude, lat: point1.latitude }, { lng: point2.longitude, lat: point2.latitude });
  return Math.round(distanceInMeters / 100) / 10;
};

const schoolSortBasedOnBoundaryAndDistance = (school1: School, school2: School, point: Point) => {
  const school1InBoundary = school1.boundaryIncludesPoint({ lng: point.longitude, lat: point.latitude });
  const school2InBoundary = school2.boundaryIncludesPoint({ lng: point.longitude, lat: point.latitude });
  if (school1InBoundary && school2InBoundary) {
    return distanceToMappable(school1.position, point) > distanceToMappable(school2.position, point) ? 1 : -1;
  } else {
    return school2InBoundary ? 1 : -1;
  }
};

export default function NearbySchools({ listing, isModal = false, schools: initialSchools, hasFetched, setHasFetched, setHasSchools }: Props) {
  const { closeAllModals } = useModalContext();
  const [schoolLevelSelected, setSchoolLevelSelected] = useState<'elementary' | 'secondary'>('elementary');
  const [schools, setSchools] = useState<School[]>(!initialSchools ? [] : initialSchools.map(school => new School(school)).sort((a, b) => schoolSortBasedOnBoundaryAndDistance(a, b, listing.position)));
  const [isVisible, setIsVisible] = useState(schools.length > 0);
  const { features } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const isZoocasaTheme = themeName === ThemeNames.ZOOCASA;

  const grabNearbySchools = async () => {
    if (hasFetched || schools?.length) {
      return new Promise<School[]>(resolve => resolve(schools));
    } else {
      if (!listing.position) {
        return null;
      }
      const params = { point: JSON.stringify(listing.position) };
      return getNearbySchools(params).then(schools => {
        const sortedSchools = schools.map(school => new School(school)).sort((a, b) => schoolSortBasedOnBoundaryAndDistance(a, b, listing.position!));
        setHasFetched(true);
        setHasSchools(!!sortedSchools.length);
        setSchools(sortedSchools);
        return sortedSchools;
      });
    }
  };

  const handleSchoolLinkClick = () => {
    closeAllModals();
    if (!isModal) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_SCHOOL_NAME);
    }
  };

  const isListingWithinBoundary = (school: School) => {
    if (listing.position) {
      return school.boundaryIncludesPoint({ lng: listing.position.longitude, lat: listing.position.latitude });
    }
  };

  const getButtonClass = (type: 'elementary' | 'secondary'): string => schoolLevelSelected === type ? styles.active : '';

  const getSchoolsInBoundary = () => {
    return (schools as unknown as School[]).reduce((schoolsWithinBoundary: School[], school) => {
      if (isListingWithinBoundary(school)) {
        schoolsWithinBoundary.push(school);
      }
      return schoolsWithinBoundary;
    }, []);
  };

  const getSchoolsOfLevelType = (schools: School[]) => schools.filter(school => school.level === schoolLevelSelected);

  const getSchoolNameListSeparator = (list: School[], index: number): ' or ' | ', ' | '. ' => {
    if (index === list.length - 1) {
      return '. ';
    } else {
      return list.length === 2 ? ' or ' : ', ';
    }
  };

  const Schools = () => {
    const schoolsWithinBoundary = getSchoolsInBoundary();
    return (
      <div className={styles.component}>
        <div className={styles.tabs}>
          <Button label="Elementary" className={buildClassName(styles.tab, getButtonClass('elementary'), isZoocasaTheme && styles['zoocasa-theme'])} onClick={() => setSchoolLevelSelected('elementary')} testId={listingPageModalIds.elementarySchoolsButton} />
          <Button label="Secondary" className={buildClassName(styles.tab, getButtonClass('secondary'), isZoocasaTheme && styles['zoocasa-theme'])} onClick={() => setSchoolLevelSelected('secondary')} testId={listingPageModalIds.secondarySchoolsButton} />
        </div>

        <p className={styles['description']}>
          Residents likely attend
          {getSchoolsOfLevelType(schoolsWithinBoundary).map((school, index) => (
            <span key={index}>
              <Link href={`/school/${school.slug}`} className={styles['likely-attend-school']} onClick={closeAllModals}>
                {''} {school.name}
              </Link>
              {getSchoolNameListSeparator(getSchoolsOfLevelType(schoolsWithinBoundary), index)}
            </span>
          ))}
          <br /><br />
          Please <a href="/school-boundary-lookup" target="_blank" rel="noopener noreferrer">confirm the school</a> for this property before making a purchasing decision.
        </p>

        {schoolsWithinBoundary.length > 0 && features.useFAQMarkUp &&
          <SchemaOrg itemProp="mainEntity" itemType="Question">
            <meta itemProp="name" content={`What are nearby schools to ${listing.seoStreet}, ${listing.city}?`} />
            <SchemaOrg itemProp="acceptedAnswer" itemType="Answer">
              <meta itemProp="text" content={schoolsWithinBoundary.map(e => e.name).join(', ')} />
            </SchemaOrg>
          </SchemaOrg>
        }
        <div className={styles['title-row']}>
          <span className={styles['name-column']}>Name</span>
          <span className={styles['centered-cell']}>Rating</span>
          <span className={styles['centered-cell']}>In Boundary</span>
        </div>

        <section className={styles['table']}>
          {getSchoolsOfLevelType(schools).map((school: School, index) => (
            <div className={styles['school-row']} key={index}>
              <div className={styles['name-column']}>
                <Link href={`/school/${school.slug}`} className={styles['school-row-name']} onClick={handleSchoolLinkClick}>
                  {school.name}
                </Link>
                <div className={styles['school-row-details']}>
                  ({school.grades})&nbsp;
                  {listing.position && distanceToMappable(school.position, listing.position) + 'km Away'}
                </div>
              </div>
              <div className={styles['school-row-rating']}>
                {school.eqaoRating &&
                  <div><span>{(school.eqaoRating)?.toFixed(1)}</span>/4</div>
                }
                {!school.eqaoRating && <span>-</span>}
              </div>
              <div className={`${styles['centered-cell']} ${styles['boundary-status']}`}>
                {school.boundary
                  ? <>{isListingWithinBoundary(school) ?
                    <CheckmarkCircleIcon className={styles['icon-check-circle']} /> :
                    <CloseCircleIcon className={styles['icon-x-circle']} />}</>
                  : <span>-</span>
                }
              </div>
            </div>
          ))}
        </section>
      </div>
    );
  };

  return (!initialSchools?.length ?
    <LoadWhenVisible onValueChange={() => setIsVisible(true)}>
      <>
        {isVisible ?
          <LoadingData promise={grabNearbySchools() as Promise<School[]>} emptyState='No Schools nearby'>
            {Schools}
          </LoadingData>
          : <LoadingSkeleton height="15.7em" />
        }
      </>
    </LoadWhenVisible> : Schools());
}
